/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import { IModelConnection, ScreenViewport, SelectionSetEvent } from "@itwin/core-frontend"; //!!OA
import { ISelectionProvider, Presentation, SelectionChangeEventArgs } from "@itwin/presentation-frontend";

import { cQuery } from "./Query";
import { cMessage } from "./Message";

export class cSelection {
  private static _onSelectionChanged = new Map<string, any>();
  private static _onElementSelected = new Map<string, any>();
  private static iModel: IModelConnection;
  private static sViewPort: ScreenViewport;

  public static initSelection = async (vp: ScreenViewport, imodel: IModelConnection) => {
    this.iModel = imodel;
    this.sViewPort = vp;

    if (!this.iModel.selectionSet.onChanged.has(this.handleElementSelected)) {
      this.iModel.selectionSet.onChanged.addListener(this.handleElementSelected);
    }
    this.setOnElementSelected("selectionInfo", this.selectionInfo);
  }

  public static setOnSelectionChanged = (eventId: string, callback?: (elementId: string) => void) => {
    if (callback) {
    // subscribe for selection changes
      this._onSelectionChanged.set(eventId, callback);
    } else {
      this._onSelectionChanged.delete(eventId);
    }
  }

  public static setOnElementSelected = (eventId: string, callback?: (elementId: string[]) => void) => {
    if (callback) {
      // subscribe for element selection
      this._onElementSelected.set(eventId, callback);
    } else {
      this._onElementSelected.delete(eventId);
    }
  }

  public static getSelections = (): Set<string> => {
    let elements: Set<string>;

    if (this.iModel) {
      elements = this.iModel.selectionSet.elements;
    }

    return elements!;
  }

  public static getSelectionCount = (): number => {
    let count = 0;
    if (this.iModel) {
      count = this.iModel.selectionSet.size;
    }

    return count;
  }

  public static getSelectedTags = async (): Promise<string[]> => {
    let elementTags: string[] = [];

    const elementIds = Array.from(this.getSelections())!;
    if (elementIds !== undefined) {
      elementTags = await cQuery.getElementTag(elementIds);
    }

    return elementTags;
  }


  private static handleSelectionChanged = async (ev: SelectionChangeEventArgs, selectionProvider: ISelectionProvider) => {
    const elements = this.getSelections();
    if (elements) {
      //    cMessage.showInfo(`Selection Changed: ${elementId}`);
      for (const [eventId, callback] of this._onSelectionChanged) {
        if (callback) {
          callback(elements);
        }
      }
    }
  }

  private static handleElementSelected = async (ev: SelectionSetEvent) => {
    const elementIds = Array.from(ev.set.elements)!;
    if (elementIds !== undefined) {
      const elementTags = await cQuery.getElementTag(elementIds);
      for (const [eventId, callback] of this._onElementSelected) {
        if (callback) {
          try {
            await callback(elementTags);
          } catch (err) {
            console.log(err);
          }
        }
      }
    }
  }

  public static async selectElementByTag(elementTag: string, match?: boolean, clear?: boolean): Promise<string[] | undefined> {

    const elementIds = await cQuery.getElementIdFromTag(elementTag, match);
    if (elementIds) {
      this.selectElement(elementIds, clear);
    }

    return elementIds;
  }

  public static async removeElementByTag(elementTag: string): Promise<string[] | undefined> {

    const elementIds = await cQuery.getElementIdFromTag(elementTag, true);
    if (elementIds) {
      this.removeElement(elementIds);
    }

    return elementIds;
  }

  public static selectElement(elementIds: string[], clear: boolean = true) {

    if (clear) this.clearSelection();
    if (elementIds) {
      this.iModel.selectionSet.add(elementIds);
      this.setHilite(elementIds, true);
    }
  }

  public static removeElement(elementIds: string[]) {

    this.iModel.selectionSet.remove(elementIds);
    this.setHilite(elementIds, false);
  }

  public static setHilite = (elementIds: string[], clear: boolean = true) => {

    if (elementIds) {
      this.iModel.hilited.setHilite(elementIds, clear);
    }
  }

  public static clearSelection() {
    this.iModel.selectionSet.emptyAll();
    this.iModel.hilited.clear();
  }

  private static selectionInfo = async (elementIds: string[]) => {
    const elementCount = this.getSelectionCount();
    switch (elementCount) {
      case 0: {
        cMessage.showStatusBarMessage();
        break;
      }
      case 1: {
        // const elements = this.getSelections();
        // const elementId = Array.from(elements)[0];
        // const elementTag = await cQuery.getElementTag(elementId);
        const elementTag = elementIds[0];
        cMessage.showStatusBarMessage(`${elementTag} selected`, "icon-select-single");
        break;
      }
      default: {
        cMessage.showStatusBarMessage(`${elementCount} objects selected`, "icon-select-single");
      }
    }
  }

  //!!! AH
  // FOR MAKING CLIP VECTOR

  // public static async getSelectionPoints(): Promise<Point3d[]> {
  //   const points: Point3d[] = [];
  //   const elements = cSelection.getSelections();
  //   for (const elementId of elements) {
  //     const elemProps = await this.iModel.elements.loadProps(elementId, {wantGeometry: true});
  //     if (elemProps) {
  //       const itWorld = new GeometryStreamIterator(elemProps['geom'], elemProps["category"], elemProps["placement"]); 
  //       for (const entry of itWorld) {
  //         if ("partReference" === entry.primitive.type) {
  //           const partProps = await this.iModel.elements.loadProps(entry.primitive.part.id, 
  //             { wantGeometry: true });
  //           if (partProps) {
  //             const itWorldPart = new GeometryStreamIterator(partProps['geom'], 
  //               partProps['category'], partProps['placement']);
  //             for (const partEntry of itWorldPart) {
  //               console.log(partEntry.primitive);
  //             } 
  //           }
              
  
  //         }
  //       }
  //     }
  //     // const partProps = await this.iModel.elements.loadProps(elementId,
  //     //    { wantGeometry: true }) as GeometryPartProps;
  //     // if (partProps) {
  //     //   const itWorldPart = GeometryStreamIterator.fromGeometryPart(partProps,
  //     //      entry.geomParams, itWorld.partToWorld());
  //     // }
  //     // const props = await this.iModel.elements.loadProps(elementId, {wantGeometry: true});
  //     // console.log(props);
  //     // if (props){
  //     //   const itLocal = new GeometryStreamIterator(props['geom'], props["category"], props["placement"]); 
  //     //   console.log(itLocal);
  //     //   for (const entry of itLocal) { 
  //     //     console.log(entry.primitive.type);
  //     //     // assertTrue(entry.primitive.type === "geometryQuery");
  //     //     // assertTrue(entry.primitive.geometry instanceof SolidPrimitive);
  //     //   }
  //     // }
  //     const range = await cQuery.getElementRange(elementId);
  //     if (range) {
  //       points.push(range.low);
  //       points.push(new Point3d(range.xLow, range.yLow, range.zHigh));
  //       points.push(new Point3d(range.xLow, range.yHigh, range.zLow));
  //       points.push(new Point3d(range.xLow, range.yHigh, range.zHigh));
  //       points.push(new Point3d(range.xHigh, range.yLow, range.zLow));
  //       points.push(new Point3d(range.xHigh, range.yLow, range.zHigh));
  //       points.push(new Point3d(range.xHigh, range.yHigh, range.zLow));        
  //       points.push(range.high);

  //     }
  //   }
  //   return points;
  // }
  // public static generateRandomPoints(high: Point3d, low: Point3d): Point3d {
  //   const x = (Math.random() * (high.x - low.x) + high.x);
  //   const y = (Math.random() * (high.y - low.y) + high.y);
  //   const z = (Math.random() * (high.z - low.z) + high.z);
  //   const point: Point3d = new Point3d(x,y,z);
  //   return point;
  // }

}
